/**
 * URL Unlocking Form Component
 * Supports Easy mode (single URL input) and Advanced mode (separate path inputs)
 */

import React, { useState, useCallback, useEffect } from 'react';
import { Form, Button, Radio, Input, Dropdown, Message, Segment } from 'semantic-ui-react';
import { I18n, Translate } from 'react-redux-i18n';
import { FormMode, UrlTargetType } from '../types';
import { parseFullUrl, validateInputs } from '../api';
import { i18nKeys, VALID_DOMAIN } from '../constants';
import styles from '../UrlUnlocking.module.css';

export interface UrlUnlockingFormInitialValues {
  mode?: FormMode;
  targetType?: UrlTargetType | '';
  fullUrl?: string;
  primaryPath?: string;
  secondaryPath?: string;
  tertiaryPath?: string;
}

export interface UrlUnlockingSearchArgs {
  targetType: UrlTargetType;
  primary: string;
  secondary: string;
  tertiary: string;
  mode: FormMode;
  fullUrl: string;
}

interface UrlUnlockingFormProps {
  onSearch: (args: UrlUnlockingSearchArgs) => void;
  onClear: () => void;
  isLoading: boolean;
  initialValues?: UrlUnlockingFormInitialValues;
}

const targetTypeOptions = [
  { key: '', value: '', text: I18n.t(i18nKeys.FORM.TARGET_TYPE_PLACEHOLDER) },
  { key: 'primary', value: 'primary', text: I18n.t(i18nKeys.FORM.TARGET_PRIMARY) },
  { key: 'secondary', value: 'secondary', text: I18n.t(i18nKeys.FORM.TARGET_SECONDARY) },
  { key: 'tertiary', value: 'tertiary', text: I18n.t(i18nKeys.FORM.TARGET_TERTIARY) }
];

const UrlUnlockingForm: React.FC<UrlUnlockingFormProps> = ({
  onSearch,
  onClear,
  isLoading,
  initialValues
}) => {
  const [mode, setMode] = useState<FormMode>(initialValues?.mode ?? 'easy');
  const [targetType, setTargetType] = useState<UrlTargetType | ''>(initialValues?.targetType ?? '');
  const [fullUrl, setFullUrl] = useState(initialValues?.fullUrl ?? '');
  const [primaryPath, setPrimaryPath] = useState(initialValues?.primaryPath ?? '');
  const [secondaryPath, setSecondaryPath] = useState(initialValues?.secondaryPath ?? '');
  const [tertiaryPath, setTertiaryPath] = useState(initialValues?.tertiaryPath ?? '');
  const [validationError, setValidationError] = useState<string | null>(null);

  // Auto-parse URL in easy mode
  useEffect(() => {
    if (mode === 'easy' && fullUrl) {
      const parsed = parseFullUrl(fullUrl);
      if (parsed.isValid && parsed.isValidDomain) {
        setPrimaryPath(parsed.primary);
        setSecondaryPath(parsed.secondary);
        setTertiaryPath(parsed.tertiary);
        setValidationError(null);
      } else if (parsed.error === 'invalidDomain') {
        setValidationError(I18n.t(i18nKeys.FORM.VALIDATION_INVALID_DOMAIN, { domain: VALID_DOMAIN }));
      } else if (parsed.error === 'invalidUrl') {
        setValidationError(I18n.t(i18nKeys.FORM.VALIDATION_INVALID_URL));
      }
    }
  }, [mode, fullUrl]);

  const handleModeChange = useCallback((newMode: FormMode) => {
    setMode(newMode);
    setValidationError(null);
  }, []);

  const handleTargetTypeChange = useCallback((_e: any, { value }: any) => {
    setTargetType(value as UrlTargetType | '');
    setValidationError(null);
  }, []);

  const handleSearch = useCallback(() => {
    setValidationError(null);

    // Validate target type is selected
    if (!targetType) {
      setValidationError(I18n.t(i18nKeys.FORM.VALIDATION_MISSING_TARGET_TYPE));
      return;
    }

    // Validate in easy mode
    if (mode === 'easy') {
      const parsed = parseFullUrl(fullUrl);
      if (!parsed.isValid) {
        setValidationError(I18n.t(i18nKeys.FORM.VALIDATION_INVALID_URL));
        return;
      }
      if (!parsed.isValidDomain) {
        setValidationError(I18n.t(i18nKeys.FORM.VALIDATION_INVALID_DOMAIN, { domain: VALID_DOMAIN }));
        return;
      }
    }

    // Validate required inputs based on target type
    const validation = validateInputs(targetType, primaryPath, secondaryPath, tertiaryPath);
    if (!validation.isValid) {
      switch (validation.error) {
        case 'missingPrimary':
          setValidationError(I18n.t(i18nKeys.FORM.VALIDATION_MISSING_PRIMARY));
          break;
        case 'missingSecondary':
          setValidationError(I18n.t(i18nKeys.FORM.VALIDATION_MISSING_SECONDARY));
          break;
        case 'missingTertiary':
          setValidationError(I18n.t(i18nKeys.FORM.VALIDATION_MISSING_TERTIARY));
          break;
        default:
          setValidationError('Validation error');
      }
      return;
    }

    onSearch({
      targetType,
      primary: primaryPath,
      secondary: secondaryPath,
      tertiary: tertiaryPath,
      mode,
      fullUrl
    });
  }, [mode, fullUrl, targetType, primaryPath, secondaryPath, tertiaryPath, onSearch]);

  const handleClear = useCallback(() => {
    setFullUrl('');
    setPrimaryPath('');
    setSecondaryPath('');
    setTertiaryPath('');
    setTargetType('');
    setValidationError(null);
    onClear();
  }, [onClear]);

  const showSecondaryInput = targetType === 'secondary' || targetType === 'tertiary';
  const showTertiaryInput = targetType === 'tertiary';

  return (
    <Segment className={styles.formSegment}>
      <Form>
        {/* Mode Toggle */}
        <Form.Field>
          <label><Translate value={i18nKeys.FORM.MODE_LABEL} /></label>
          <div className={styles.modeToggle}>
            <Radio
              label={I18n.t(i18nKeys.FORM.MODE_EASY)}
              name="mode"
              value="easy"
              checked={mode === 'easy'}
              onChange={() => handleModeChange('easy')}
            />
            <Radio
              label={I18n.t(i18nKeys.FORM.MODE_ADVANCED)}
              name="mode"
              value="advanced"
              checked={mode === 'advanced'}
              onChange={() => handleModeChange('advanced')}
              style={{ marginLeft: '20px' }}
            />
          </div>
        </Form.Field>

        {/* Target Type Dropdown */}
        <Form.Field>
          <label><Translate value={i18nKeys.FORM.TARGET_TYPE_LABEL} /></label>
          <Dropdown
            selection
            options={targetTypeOptions}
            value={targetType}
            onChange={handleTargetTypeChange}
            placeholder={I18n.t(i18nKeys.FORM.TARGET_TYPE_PLACEHOLDER)}
            fluid
          />
        </Form.Field>

        {/* Easy Mode - Full URL Input */}
        {mode === 'easy' && (
          <Form.Field>
            <label><Translate value={i18nKeys.FORM.FULL_URL_LABEL} /></label>
            <Input
              placeholder={I18n.t(i18nKeys.FORM.FULL_URL_PLACEHOLDER)}
              value={fullUrl}
              onChange={(e) => setFullUrl(e.target.value)}
              fluid
            />
          </Form.Field>
        )}

        {/* Advanced Mode / Parsed Values Display */}
        {mode === 'advanced' && (
          <>
            <Form.Field required>
              <label><Translate value={i18nKeys.FORM.PRIMARY_PATH_LABEL} /></label>
              <Input
                placeholder={I18n.t(i18nKeys.FORM.PRIMARY_PATH_PLACEHOLDER)}
                value={primaryPath}
                onChange={(e) => setPrimaryPath(e.target.value)}
                fluid
              />
            </Form.Field>

            {showSecondaryInput && (
              <Form.Field required>
                <label><Translate value={i18nKeys.FORM.SECONDARY_PATH_LABEL} /></label>
                <Input
                  placeholder={I18n.t(i18nKeys.FORM.SECONDARY_PATH_PLACEHOLDER)}
                  value={secondaryPath}
                  onChange={(e) => setSecondaryPath(e.target.value)}
                  fluid
                />
              </Form.Field>
            )}

            {showTertiaryInput && (
              <Form.Field required>
                <label><Translate value={i18nKeys.FORM.TERTIARY_PATH_LABEL} /></label>
                <Input
                  placeholder={I18n.t(i18nKeys.FORM.TERTIARY_PATH_PLACEHOLDER)}
                  value={tertiaryPath}
                  onChange={(e) => setTertiaryPath(e.target.value)}
                  fluid
                />
              </Form.Field>
            )}
          </>
        )}

        {/* Parsed preview in easy mode */}
        {mode === 'easy' && (primaryPath || secondaryPath || tertiaryPath) && (
          <div className={styles.parsedPreview}>
            <strong>Parsed values:</strong>
            <div>Primary: <code>{primaryPath || '(empty)'}</code></div>
            {showSecondaryInput && <div>Secondary: <code>{secondaryPath || '(empty)'}</code></div>}
            {showTertiaryInput && <div>Tertiary: <code>{tertiaryPath || '(empty)'}</code></div>}
          </div>
        )}

        {/* Validation Error */}
        {validationError && (
          <Message negative>
            <p>{validationError}</p>
          </Message>
        )}

        {/* Action Buttons */}
        <div className={styles.formActions}>
          <Button
            primary
            onClick={handleSearch}
            loading={isLoading}
            disabled={isLoading}
          >
            <Translate value={i18nKeys.FORM.SEARCH_BUTTON} />
          </Button>
          <Button
            onClick={handleClear}
            disabled={isLoading}
          >
            <Translate value={i18nKeys.FORM.CLEAR_BUTTON} />
          </Button>
        </div>
      </Form>
    </Segment>
  );
};

export default UrlUnlockingForm;
